import { useFormState } from "common/hooks";
import { ClientUser } from "common/types";
import { MONTH_SHORT_NAMES } from "common/constants/dates";
import { dateParts, toDateFromParts } from "common/utils/dates";

import { OnboardingSubscriptionsStepParams } from "client/onboarding/types";
import Button from "client/common/components/buttons/Button";
import TextInput from "client/common/components/inputs/TextInput";
import { useUpdateProfileMutation } from "client/accounts/hooks";
import FetchErrorAlert from "client/common/components/errors/FetchErrorAlert";
import { useControlledSubstancesInfoDrawer } from "client/accounts/stackables";
import TextButton from "client/common/components/buttons/TextButton";
import StateSelect from "client/common/components/inputs/StateSelect";
import Select from "client/common/components/inputs/Select";
import { useInvalidateOnboarding } from "client/onboarding/hooks/invalidate";

import StepLayout from "../OnboardingSubscriptionsStepLayout";

import styles from "../../styles/OnboardingSubscriptionsControlledSubstancesStep.module.less";

type Values = {
  drivers_license_number: string;
  drivers_license_state: string;
  birth_month: string;
  birth_day: string;
  birth_year: string;
};

const getInitialValues = (clientUser: ClientUser): Values => {
  const { day, month, year } = clientUser.date_of_birth
    ? dateParts(clientUser.date_of_birth)
    : { day: "", month: "", year: "" };
  return {
    drivers_license_number: clientUser.drivers_license_number ?? "",
    drivers_license_state: clientUser.drivers_license_state ?? "",
    birth_day: `${day}`,
    birth_month: `${month}`,
    birth_year: `${year}`,
  };
};

const OnboardingSubscriptionsControlledSubstancesStep = ({
  onNext,
  isLoadingNext,
  client,
  onboarding,
  stepper,
}: OnboardingSubscriptionsStepParams) => {
  const [values, updateField] = useFormState(getInitialValues(client.primary_client_user));
  const invalidateOnboarding = useInvalidateOnboarding();

  const {
    mutate: updateProfile,
    isLoading: isUpdating,
    isSuccess: isUpdateSuccess,
    error: updateError,
  } = useUpdateProfileMutation({
    onSuccess: () => {
      invalidateOnboarding();
      onNext();
    },
  });
  const { open: openControlledSubstancesInfoDrawer } = useControlledSubstancesInfoDrawer();

  const requiredFields = onboarding.controlled_substances_required_fields;
  const requiresDLNumber = requiredFields.includes("drivers_license_number");
  const requiresDLState = requiredFields.includes("drivers_license_state");
  const requiresDOB = requiredFields.includes("date_of_birth");
  const hasRequiredFields = !!(
    (!requiresDLNumber || values.drivers_license_number) &&
    (!requiresDLState || values.drivers_license_state) &&
    (!requiresDOB || (values.birth_day && values.birth_month && values.birth_year))
  );
  const canSubmit = !isUpdating && !isLoadingNext && !isUpdateSuccess && hasRequiredFields;

  const onSubmit = () => {
    const { birth_day: day, birth_month: month, birth_year: year, ...rest } = values;
    updateProfile({
      ...(requiresDOB ? { date_of_birth: toDateFromParts({ year, month, day }) } : {}),
      ...rest,
    });
  };

  return (
    <StepLayout stepper={stepper}>
      <StepLayout.Title>Since you have a controlled substance...</StepLayout.Title>
      <StepLayout.Description>
        <div>
          Some additional information is required because an Autoship item is a controlled
          substance. Please note an adult signature will be required for delivery.{" "}
          <TextButton hasUnderline={true} icon="right" onClick={openControlledSubstancesInfoDrawer}>
            Learn more
          </TextButton>
        </div>
      </StepLayout.Description>
      {updateError && <FetchErrorAlert error={updateError} />}
      <StepLayout.Form onSubmit={onSubmit} canSubmit={canSubmit}>
        {requiresDLNumber && (
          <TextInput
            label="Driver's license ID"
            value={values.drivers_license_number ?? ""}
            onChange={updateField("drivers_license_number")}
          />
        )}
        {requiresDLState && (
          <StateSelect
            label="Driver's license state"
            value={values.drivers_license_state ?? ""}
            onChange={updateField("drivers_license_state")}
          />
        )}
        {requiresDOB && (
          <div className={styles.dob}>
            <Select
              label="Birth month"
              value={values.birth_month}
              onChange={updateField("birth_month")}
              className={styles.month}
            >
              {MONTH_SHORT_NAMES.map((monthName, index) => (
                <Select.Option value={`${index + 1}`} key={monthName}>
                  {monthName}
                </Select.Option>
              ))}
            </Select>
            <TextInput
              type="number"
              label="Birth day"
              value={values.birth_day}
              maxLength={2}
              onChange={updateField("birth_day")}
            />
            <TextInput
              type="number"
              label="Birth year"
              value={values.birth_year}
              maxLength={4}
              onChange={updateField("birth_year")}
            />
          </div>
        )}
        <StepLayout.Footer>
          <Button type="submit" disabled={!canSubmit} isLoading={isUpdating || isLoadingNext}>
            Next
          </Button>
        </StepLayout.Footer>
      </StepLayout.Form>
    </StepLayout>
  );
};

export default OnboardingSubscriptionsControlledSubstancesStep;
